import { useState, type ReactNode } from 'react';
import { ArrowRightLeft, Cable, CircleAlert, Cpu, KeyRound, Pause, Play, Server } from 'lucide-react';
import { Banner, BrandIcon, Button, Card, Fact, Facts, ProgressBar, StatusPill } from '@/components/ui';
import { AgentSetup } from '@/components/nodes/AgentSetup';
import { DrainNodeDialog } from '@/components/nodes/DrainNodeDialog';
import { NodeRemnants } from '@/components/nodes/NodeRemnants';
import { RevokeAgentDialog } from '@/components/nodes/RevokeAgentDialog';
import { useRoles } from '@/hooks/useAuth';
import { useReissueEnrollmentToken, useSetNodeScheduling } from '@/hooks/useNodes';
import { toast } from '@/store/toastStore';
import type { EnrollmentIssued, NodeDetailView } from '@/types/api';
import { errorText } from '@/utils/errors';
import { formatDateTime, formatGb, formatPercent, formatRelative } from '@/utils/format';
import { NODE_STATE } from '@/utils/labels';
import { nodeState } from '@/utils/nodes';
import { can } from '@/utils/permissions';

/** How full one resource is, as a bar that turns when it is nearly out. */
function Usage({ label, used, total, text }: { readonly label: string; readonly used: number; readonly total: number; readonly text: string }): ReactNode {
  const ratio = total > 0 ? used / total : 1;
  return (
    <div className="stack stack-tight">
      <div className="row-between text-sm">
        <span>{label}</span>
        <span className="muted numeric">{text}</span>
      </div>
      <ProgressBar value={used} max={Math.max(total, 1)} label={label} tone={ratio >= 0.9 ? 'down' : ratio >= 0.75 ? 'degraded' : 'accent'} />
    </div>
  );
}

/** What serves the sites: Docker on the panel's own server, the web servers the agent found on an external one. */
export function ServerKind({ node }: { readonly node: Pick<NodeDetailView, 'role' | 'discovery'> }): ReactNode {
  if (node.role !== 'host') {
    return (
      <span className="platform-name">
        <BrandIcon name="docker" size={14} />
        сервер панели · Docker
      </span>
    );
  }
  const servers = node.discovery?.webServers ?? (node.discovery?.nginx ? ['nginx'] : []);
  return (
    <span className="platform-name">
      {servers.map((server) => (
        <BrandIcon key={server} name={server === 'apache' ? 'apache' : server === 'docker' ? 'docker' : 'nginx'} size={14} />
      ))}
      внешний сервер{servers.length > 0 ? ` · ${servers.map((server) => (server === 'apache' ? 'Apache' : server === 'docker' ? 'Docker' : 'nginx')).join(', ')}` : ''}
    </span>
  );
}

/**
 * A server's first tab (ADR-0058): what the machine is, how full it is, its
 * agent, and what can be done to it — the facts and actions the drawer on
 * the servers list used to hold.
 */
export function ServerOverview({ node }: { readonly node: NodeDetailView }): ReactNode {
  const roles = useRoles();
  const [issued, setIssued] = useState<EnrollmentIssued | null>(null);
  const [draining, setDraining] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const reissue = useReissueEnrollmentToken({ onSuccess: (result) => setIssued(result) });
  const scheduling = useSetNodeScheduling({
    onSuccess: (result) => toast.success(result.schedulingState === 'cordoned' ? 'Сервер закрыт для новых сайтов' : 'Сервер открыт для новых сайтов'),
  });

  const status = nodeState(node);
  const awaiting = status === 'awaiting';
  const mayManage = can(roles, 'nodes.manage');
  const host = node.role === 'host';
  const machine = node.machine;
  const addresses = [node.ipv4, machine?.ipv6].filter(Boolean);

  return (
    <div className="stack">
      {issued ? <AgentSetup issued={issued} /> : null}

      {awaiting && !issued && can(roles, 'nodes.enroll') ? (
        <Banner tone="info" icon={KeyRound}>
          <div className="stack stack-tight">
            <span>Агент ещё не подключался. Новый токен отменяет прежний.</span>
            <div className="row-tight">
              <Button size="sm" icon={KeyRound} busy={reissue.isPending} onClick={() => reissue.mutate(node.id)}>
                Новый токен
              </Button>
            </div>
            {reissue.isError ? <p className="field-error">{errorText(reissue.error)}</p> : null}
          </div>
        </Banner>
      ) : null}

      {node.agentIssue && !node.stale ? (
        <Banner tone="warning" icon={CircleAlert}>
          <span>
            Ошибка агента: <span className="mono">{node.agentIssue}</span>. Операции на сервере приостановлены.
          </span>
        </Banner>
      ) : null}

      {node.stale && !awaiting ? (
        <Banner tone="warning" icon={CircleAlert}>
          Агент не отвечает более двух минут. Операции на сервере недоступны, состояние сайтов неизвестно.
        </Banner>
      ) : null}

      {node.schedulingState === 'draining' ? (
        <Banner tone="info" icon={ArrowRightLeft}>
          Сайты переносятся на другие серверы.
          {node.siteCount > 0 ? ` Осталось сайтов: ${node.siteCount}.` : ''}
        </Banner>
      ) : null}

      <div className="split">
        <Card title="Сервер" icon={Server}>
          <Facts>
            <Fact label="Платформа">
              <ServerKind node={node} />
            </Fact>
            <Fact label="IP">
              {addresses.length > 0 ? (
                <span className="stack stack-tight mono">
                  {addresses.map((address) => (
                    <span key={address}>{address}</span>
                  ))}
                </span>
              ) : (
                <span className="faint">—</span>
              )}
            </Fact>
            <Fact label="Имя хоста">
              <span className="mono">{machine?.hostname ?? node.hostname ?? node.discovery?.hostname ?? '—'}</span>
            </Fact>
            {machine ? (
              <Fact label="В 2d2s">
                {[machine.displayName, machine.datacenter, machine.status.toLowerCase()].filter(Boolean).join(' · ')}
                <span className="muted"> · сверено {formatRelative(machine.syncedAt)}</span>
              </Fact>
            ) : (
              <Fact label="В 2d2s">
                <span className="faint">{host ? 'сервер вне инвентаря 2d2s' : 'нет данных'}</span>
              </Fact>
            )}
            {!host ? (
              <Fact label="Метки">
                <span className="mono">{node.labels.length > 0 ? node.labels.join(', ') : '—'}</span>
              </Fact>
            ) : null}
            <Fact label="Подключён">{formatDateTime(node.createdAt)}</Fact>
            <Fact label="Идентификатор">
              <span className="mono text-sm">{node.id}</span>
            </Fact>
          </Facts>
        </Card>

        <Card title="Ресурсы" icon={Cpu}>
          <div className="stack">
            <Facts>
              <Fact label="Процессор">{machine?.vcpuCount ? `${machine.vcpuCount} vCPU` : <span className="faint">не сообщается</span>}</Fact>
            </Facts>
            <Usage
              label="Память"
              used={node.usedMemoryMb}
              total={node.totalMemoryMb}
              text={`${(node.usedMemoryMb / 1024).toFixed(1)} из ${(node.totalMemoryMb / 1024).toFixed(1)} ГБ · ${formatPercent(node.usedMemoryMb, node.totalMemoryMb)}`}
            />
            <Usage label="Диск" used={node.usedDiskGb} total={node.totalDiskGb} text={`${formatGb(node.usedDiskGb)} из ${formatGb(node.totalDiskGb)} · ${formatPercent(node.usedDiskGb, node.totalDiskGb)}`} />
            {host ? null : <Usage label="Сайты" used={node.siteCount} total={node.maxSites} text={`${node.siteCount} из ${node.maxSites}`} />}
            {host ? null : (
              <p className="field-hint">
                В запасе для восстановления: {(node.reservedMemoryMb / 1024).toFixed(0)} ГБ памяти и {formatGb(node.reservedDiskGb)} диска — новые сайты их не занимают.
              </p>
            )}
          </div>
        </Card>
      </div>

      <Card title="Агент" icon={Cable}>
        <div className="stack">
          <Facts>
            <Fact label="Состояние">
              <StatusPill map={NODE_STATE} status={status} />
            </Fact>
            <Fact label="Версия">
              <span className="mono">{node.agentVersion ?? 'не сообщалась'}</span>
            </Fact>
            <Fact label="Последний отклик">{formatRelative(node.lastHeartbeatAt)}</Fact>
            {host ? null : (
              <Fact label="Порты 80 и 443">
                {node.firewall === 'cloudflare-only' ? (
                  'только Cloudflare и панель'
                ) : node.firewall === 'open' ? (
                  <span className="tone-degraded">открыты всем</span>
                ) : (
                  <span className="faint">неизвестно</span>
                )}
              </Fact>
            )}
          </Facts>

          {mayManage && !awaiting ? (
            <div className="row-tight row-wrap">
              {!host && node.schedulingState === 'active' ? (
                <Button size="sm" icon={Pause} busy={scheduling.isPending} onClick={() => scheduling.mutate({ id: node.id, state: 'cordoned' })}>
                  Не размещать новые сайты
                </Button>
              ) : null}
              {!host && node.schedulingState === 'cordoned' ? (
                <Button size="sm" icon={Play} busy={scheduling.isPending} onClick={() => scheduling.mutate({ id: node.id, state: 'active' })}>
                  Снова принимать сайты
                </Button>
              ) : null}
              {!host && (node.schedulingState === 'active' || node.schedulingState === 'cordoned') && node.siteCount > 0 ? (
                <Button size="sm" variant="danger" icon={ArrowRightLeft} onClick={() => setDraining(true)}>
                  Перенести все сайты
                </Button>
              ) : null}
              <Button size="sm" variant="ghost" icon={KeyRound} onClick={() => setRevoking(true)}>
                Отозвать ключ агента
              </Button>
              {scheduling.isError ? <span className="field-error">{errorText(scheduling.error)}</span> : null}
            </div>
          ) : null}

          <p className="field-hint">
            {host ? 'Панель только читает сайты сервера и создаёт их копии.' : 'Питание, перезагрузка и теги сервера управляются в 2d2s.'}
          </p>
        </div>
      </Card>

      {!awaiting && !host ? <NodeRemnants nodeId={node.id} mayRemove={mayManage} /> : null}

      {mayManage ? <DrainNodeDialog node={node} open={draining} onClose={() => setDraining(false)} /> : null}
      {mayManage ? <RevokeAgentDialog node={node} open={revoking} onClose={() => setRevoking(false)} /> : null}
    </div>
  );
}
